import {Moment, unitOfTime} from 'moment';
import momentTimezone, {MomentInput} from 'moment-timezone';

import {Timezone} from 'common/constants';
import {Translatable, TranslationKey} from 'common/i18n';
import {TimePeriodSettings} from 'common/report/stores/types';
import {convertRelativeDateToCalendarDateStr, NUMBER_OF_DAYS_IN_UNIT, SettingWeekday} from 'common/util/date';
import {SPECIAL_DATE_RESOLVERS, SpecialDateString} from 'common/widgets/date-range-picker/models/presets';

import {
  BaseDate,
  DateRangeAttributes,
  DateRangeType,
  DateType,
  RelativeDate,
  StandardTimeUnit,
  TimeUnit,
  TimeUnitLabelTranslatable,
} from './types';

/**
 *
 * @param dateRange
 * @param asOfDate
 * @param projectTz
 * @param timePeriodSettings
 * @returns the inclusive number of calendar days of a date range.
 *
 * For examples:
 *  Last 7 Days will return 8 days because we count the current day separately.
 *  Between 11/09 - 11/10 will return 2 days.
 *  On 11/3 will return 1 day.
 */
export function getNumberOfCalendarDays(
  dateRange: DateRangeAttributes,
  asOfDate: Moment,
  projectTz: Timezone,
  timePeriodSettings: TimePeriodSettings,
): number {
  let fromDate: string | Date | null = convertBaseDateToCalendarDate(dateRange.from);
  if (fromDate && fromDate in SPECIAL_DATE_RESOLVERS) {
    fromDate = SPECIAL_DATE_RESOLVERS[fromDate as SpecialDateString](asOfDate.toDate(), projectTz, timePeriodSettings);
  }
  let toDate: string | Date | null = convertBaseDateToCalendarDate(dateRange.to);
  if (toDate && toDate in SPECIAL_DATE_RESOLVERS) {
    toDate = SPECIAL_DATE_RESOLVERS[toDate as SpecialDateString](asOfDate.toDate(), projectTz, timePeriodSettings);
  }
  // Left-align fromDate to SOD and right-align toDate to EOD to count full calendar dates.
  const startOfFromDate = momentTimezone.tz(fromDate as MomentInput, projectTz).startOf(`day`);
  const endOfToDate = momentTimezone.tz(toDate as MomentInput, projectTz).endOf(`day`);

  switch (dateRange.type) {
    case DateRangeType.RelativeAfter:
      if (!dateRange.window) {
        throw new Error(`window required for RelativeAfter date ranges.`);
      }
      return Math.ceil(dateRange.window.value * NUMBER_OF_DAYS_IN_UNIT[dateRange.window.unit as StandardTimeUnit]) + 1;
    case DateRangeType.Since: {
      // Right-align asOfDate to count full calendar dates.
      const endOfAsOfDate = momentTimezone.tz(asOfDate, projectTz).endOf(`day`);
      return endOfAsOfDate.diff(startOfFromDate, `days` as unitOfTime.Diff) + 1;
    }
    case DateRangeType.On:
      return 1;
    case DateRangeType.Between:
      return endOfToDate.diff(startOfFromDate, `days` as unitOfTime.Diff) + 1;
    default:
      throw new Error(`Not implemented for type ${dateRange.type}`);
  }
}

export function convertBaseDateToCalendarDate(baseDate?: BaseDate | null): string | null {
  if (!baseDate) {
    return null;
  }
  if (isRelativeDate(baseDate)) {
    return convertRelativeDateToCalendarDateStr(baseDate);
  }
  return baseDate;
}

export function isRelativeDate(date: BaseDate): date is RelativeDate {
  return Object.prototype.hasOwnProperty.call(date ?? {}, `unit`);
}

export function getDateType(date: BaseDate | null | undefined): DateType {
  if (!date) {
    return `null`;
  }
  if (isRelativeDate(date)) {
    return `relative`;
  }
  return typeof date === `string` && date in SPECIAL_DATE_RESOLVERS ? `special_literal` : `absolute`;
}

export function getMonthLabel(month: number): Translatable {
  // make a stub date to translate via formatDate
  const date = new Date();
  // Set to first of month to avoid edge cases with month lengths
  // e.g. if today is Jan 31 and we set month to April, date becomes May 1
  date.setDate(1);
  date.setMonth(month);
  return ({formatDate}) => formatDate(date, {month: `long`});
}

export function getUnitStartTooltip(unit: TimeUnit, timePeriodSettings: TimePeriodSettings): Translatable {
  switch (unit) {
    case TimeUnit.Week:
      return ({t, print}) =>
        t(`reports.date-range.presets.tooltip.unit-start`, {
          unit: print(TimeUnitLabelTranslatable[unit](1)),
          start: t(
            `reports.day-of-week.${SettingWeekday[timePeriodSettings.weekStartDay].toLowerCase()}` as TranslationKey,
          ),
        });
    case TimeUnit.Quarter:
    case TimeUnit.Year:
      return ({t, print}) =>
        t(`reports.date-range.presets.tooltip.unit-start`, {
          unit: print(TimeUnitLabelTranslatable[unit](1)),
          start: print(getMonthLabel(timePeriodSettings.quarterStartMonth)),
        });
  }
  return ``;
}
